import { useContext, useMemo } from 'react'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'
import useSearchBurnPoints from 'hooks/useSearchBurnPoints'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltySpendPointsPricingContext from 'loyalty/contexts/LoyaltySpendPointsPricingContext'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'

interface Params {
  price: number
  memberPrice?: number
  loyaltyProductType?: App.LoyaltyProductType
  mx?: string
  viewPriceInPoints?: boolean
  disabled?: boolean
  isLuxPlusEnabled?: boolean
}

export default function useSearchOfferBurnPricing({
  price,
  memberPrice,
  loyaltyProductType,
  mx,
  viewPriceInPoints: viewPriceInPointsProp = false,
  disabled = false,
  isLuxPlusEnabled = true,
}: Params) {
  const { hasSelectedBurnToggle, pointsToBurn: contextPointsToBurn } = useSearchBurnEnabled()
  const { burnPointsLimit, showOffersWithinBalance } = useContext(LoyaltySpendPointsPricingContext)
  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const viewPriceInPoints = viewPriceInPointsProp && !hasSelectedBurnToggle
  const isSocietePricingActive = viewPriceInPoints || hasSelectedBurnToggle
  const showMemberPricing = isLuxPlusEnabled && !!memberPrice
  const standardBurnResult = useSearchBurnPoints(price, loyaltyProductType, mx, {
    disabled: disabled || !isLoyaltyEnabled || !isSocietePricingActive || !loyaltyProductType,
    viewPriceInPoints,
  })
  const memberBurnResult = useSearchBurnPoints(memberPrice ?? 0, loyaltyProductType, mx, {
    disabled: disabled || !isLoyaltyEnabled || !showMemberPricing || !isSocietePricingActive || !loyaltyProductType,
    viewPriceInPoints,
  })
  const shouldShowBurn = standardBurnResult.enabled
    && standardBurnResult.priceInPoints !== undefined
    && (standardBurnResult.pointsOnly || standardBurnResult.pointsToBurn !== undefined)
  const displayBurn = useMemo(() => {
    if (standardBurnResult.priceInPoints === undefined) {
      return standardBurnResult
    }

    if (burnPointsLimit === undefined) {
      return {
        ...standardBurnResult,
        hideZeroCash: showOffersWithinBalance,
      }
    }

    const pointsToBurn = Math.min(burnPointsLimit, standardBurnResult.priceInPoints)
    const pointsCoverPrice = burnPointsLimit >= standardBurnResult.priceInPoints
    return {
      ...standardBurnResult,
      pointsToBurn,
      pointsOnly: false,
      hideZeroCash: showOffersWithinBalance || pointsCoverPrice,
    }
  }, [burnPointsLimit, showOffersWithinBalance, standardBurnResult])
  const priceInPointsForEarn = showMemberPricing && memberBurnResult.priceInPoints
    ? memberBurnResult.priceInPoints
    : displayBurn.priceInPoints
  const pointsToApplyForEarn = useMemo(() => {
    if (!isSocietePricingActive || !priceInPointsForEarn) return undefined
    if (burnPointsLimit !== undefined) return Math.min(burnPointsLimit, priceInPointsForEarn)
    if (viewPriceInPoints) return priceInPointsForEarn
    if (hasSelectedBurnToggle && contextPointsToBurn !== undefined) {
      return Math.min(contextPointsToBurn, priceInPointsForEarn)
    }
    return undefined
  }, [burnPointsLimit, contextPointsToBurn, hasSelectedBurnToggle, isSocietePricingActive, priceInPointsForEarn, viewPriceInPoints])

  return {
    hasSelectedBurnToggle,
    viewPriceInPoints,
    isSocietePricingActive,
    showMemberPricing,
    burnEnabled: displayBurn.enabled,
    priceInPoints: displayBurn.priceInPoints,
    memberPriceInPoints: memberBurnResult.priceInPoints,
    pointsToBurn: displayBurn.pointsToBurn,
    pointsOnly: displayBurn.pointsOnly,
    hideZeroCash: displayBurn.hideZeroCash,
    shouldShowBurn,
    pointsToApplyForEarn,
    priceInPointsForEarn,
  }
}

export type SearchOfferBurnPricing = ReturnType<typeof useSearchOfferBurnPricing>
